/**
 * Directive to create collapsible elements with smooth animations.
 *
 * Usage:
 * - Add the `[stCollapsible]` attribute to any element you want to be collapsible.
 * - Bind a boolean value to `isCollapsed` to control the collapsed state.
 * - Optionally, pass 'vertical' or 'horizontal' to the `direction` input to control the collapse direction.
 * - Optionally, pass the width of your container to th `maxWidth` input to prevent the directive to use the dynamic width (note: only work when the direction 'horizontal')
 *
 * Inputs:
 * - `isCollapsed`: boolean (required): Controls whether the element is collapsed or expanded.
 * - `direction`: 'vertical' | 'horizontal' (optional, default: 'vertical'): Specifies the collapse direction (either 'vertical' or 'horizontal').
 * - `maxWidth`: number (optional): give the container static width if need (note: only work when the direction 'horizontal')
 * - `minWidth`: number (optional, default: '0'): set minimum of the container width (note: only work when the direction 'horizontal')
 *
 * Example:
 * ```
 * <div [stCollapsible]="isCollapsed" [direction]="'horizontal'">
 *   <!-- collapsible content -->
 * </div>
 * ```
 */
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Renderer2,
  input,
  effect,
  OnDestroy,
  HostListener,
  output,
} from '@angular/core';

@Directive({
  selector: '[stCollapsible]',
  standalone: true,
})
export class CollapsibleDirective implements OnDestroy, AfterViewInit {
  isCollapsed = input.required<boolean>();
  direction = input<'vertical' | 'horizontal'>('vertical');
  maxWidth = input<number>();
  minWidth = input<number>();

  closed = output<boolean>();

  private resizeObserver: ResizeObserver | null = null;

  readonly element: HTMLElement;

  constructor(
    readonly el: ElementRef,
    readonly renderer: Renderer2,
  ) {
    this.element = this.el.nativeElement;

    effect(
      () => {
        if (this.isCollapsed()) {
          this.expand();
        } else {
          this.collapse();
        }
      },
      { allowSignalWrites: true },
    );
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isCollapsed()) {
      // Temporarily set height to 'auto' to avoid excess space on maximize
      if (this.direction() === 'vertical') {
        this.renderer.setStyle(this.element, 'height', 'auto');
      }
      // Reapply exact height after a brief delay
      setTimeout(() => {
        this.expand();
      }, 0);
    }
  }

  ngAfterViewInit() {
    this.setInitialState();
    this.addResizeListener();
  }

  ngOnDestroy() {
    this.removeResizeListener();
  }

  private setInitialState() {
    if (this.direction() === 'horizontal') {
      this.renderer.setStyle(this.element, 'width', this.minWidth() + 'px');
    } else {
      this.renderer.setStyle(this.element, 'height', '0px');
    }
    this.renderer.setStyle(this.element, 'overflow', 'hidden');
  }

  private expand() {
    if (this.direction() === 'horizontal') {
      // Dynamically check scrollWidth for content adjustments
      const width =
        this.maxWidth() !== null || this.maxWidth() !== undefined
          ? `${this.maxWidth()}px`
          : `${this.element.scrollWidth}px`;
      this.renderer.setStyle(this.element, 'width', width);
      this.renderer.setStyle(
        this.element,
        'transition',
        'width 0.3s ease-in-out',
      );
    } else {
      // Dynamically check scrollHeight for content adjustments
      const height = `${this.element.scrollHeight}px`;
      this.renderer.setStyle(this.element, 'height', height);
      this.renderer.setStyle(
        this.element,
        'transition',
        'height 0.3s ease-in-out',
      );
    }
  }

  private collapse() {
    if (this.direction() === 'horizontal') {
      this.renderer.setStyle(this.element, 'width', `${this.minWidth()}px`);
      this.renderer.setStyle(this.element, 'overflow', 'hidden');
    } else {
      this.renderer.setStyle(this.element, 'height', '0px');
      this.renderer.setStyle(this.element, 'overflow', 'hidden');
    }

    this.closed.emit(true);
  }

  private addResizeListener() {
    this.resizeObserver = new ResizeObserver(() => {
      // Re-expand to fit content on resize
      if (this.isCollapsed()) {
        this.expand();
      } else {
        this.collapse();
      }
    });
    this.resizeObserver.observe(this.element);
  }

  private removeResizeListener() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}
